import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ColDef } from 'ag-grid-enterprise';
import { dateFilterParamsLong } from '@rcs/utils';
import { ErsGridComponent } from '@rcs/grid';
import { PolicyDocument } from '../../../models/leave.model';
import { docNameRenderer, docActionEmployeeRenderer } from '../../../utils/grid-cell-renderers';
import { formatDateLong } from '../../../utils/date.utils';
import { LeaveService } from '../../../services/leave.service';
import { DocPreviewDialogComponent, DocPreviewDialogData } from '@rcs/doc-viewer';
import { TranslocoPipe } from '@jsverse/transloco';

const NORMAL_GRID = {
  suppressCellFocus: true,
  suppressHorizontalScroll: true,
  pagination: true,
  paginationPageSize: 5,
  paginationPageSizeSelector: false,
};

@Component({
  selector: 'ers-leave-policies',
  standalone: true,
  imports: [
    TranslocoPipe,CommonModule, MatDialogModule, ErsGridComponent],
  templateUrl: './leave-policies.component.html',
  styleUrls: ['./leave-policies.component.scss'],
})
export class LeavePoliciesComponent implements OnInit {
  private leaveService = inject(LeaveService);
  private dialog       = inject(MatDialog);

  ngOnInit(): void {
    this.loadDocuments();
  }

  // ─── Load ──────────────────────────────────────────────────────────────────

  private loadDocuments(): void {
    this.leaveService.getPolicyDocuments().subscribe({
      next: (response: any) => {
        // Backend may return array directly or { data: [...] } shape
        const rows: any[] = Array.isArray(response) ? response : (response?.data ?? response?.value ?? []);
        this.policyDocuments = (rows ?? []).map((r: any) => {
          const url      = r.documentUrl || r.documentPath || r.DOCUMENT_URL || r.DOCUMENT_PATH || '';
          const fileName = r.documentTitle || r.DOCUMENT_TITLE || r.name || '';
          const ext      = fileName.split('.').pop()?.toUpperCase() ?? 'FILE';

          // Prefer explicit fields; fall back to parsing timestamp from the blob URL
          const createdOn = r.createdOn || r.CREATED_ON || r.createdAt || this.parseDateFromBlobUrl(url);
          const uploadedBy = r.createdBy || r.CREATED_BY || r.uploadedBy
                          || r.documentDescription || r.DOCUMENT_DESCRIPTION || '–';

          return {
            id:           r.id || r.ID,
            name:         fileName,
            uploadedBy,
            fileSize:     '–',
            fileType:     ext,
            uploadedDate: createdOn
              ? formatDateLong(createdOn)
              : '–',
            documentUrl:  url,
            leaveTypeId:  r.leaveTypeId || r.LEAVE_TYPE_ID || r.LeaveTypeId || null,
          };
        });

        // Resolve actual file sizes via server-side HEAD proxy (avoids browser CORS)
        this.policyDocuments.forEach((doc, idx) => {
          if (doc.documentUrl) {
            this.leaveService.getPolicyDocumentFileInfo(doc.documentUrl).subscribe({
              next: ({ contentLength }) => {
                if (contentLength != null) {
                  let size: string;
                  if (contentLength < 1024)             size = `${contentLength} B`;
                  else if (contentLength < 1024 * 1024) size = `${(contentLength / 1024).toFixed(1)} KB`;
                  else                                   size = `${(contentLength / (1024 * 1024)).toFixed(1)} MB`;
                  this.policyDocuments = this.policyDocuments.map((d, i) =>
                    i === idx ? { ...d, fileSize: size } : d
                  );
                }
              },
            });
          }
        });
      },
      error: (err) => { console.error('Failed to load policy documents', err); },
    });
  }

  /** Extract a date from the blob-storage timestamp embedded in the filename
   *  e.g. `…/fileName_20260227081506507128.pdf` → 2026-02-27 */
  private parseDateFromBlobUrl(url: string): string | null {
    const match = url.match(/_(\d{4})(\d{2})(\d{2})\d{6,}\.\w+$/);
    if (!match) return null;
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  // ─── Computed ───────────────────────────────────────────────────────────────

  // ─── Policy-documents grid ─────────────────────────────────────────────────

  policyDocuments: PolicyDocument[] = [];

  docColDefs: ColDef[] = [
    { headerName: 'Name',          field: 'name',         flex: 2, filter: 'agSetColumnFilter', cellRenderer: docNameRenderer },
    { headerName: 'Uploaded By',   field: 'uploadedBy',   flex: 2, filter: 'agSetColumnFilter', cellStyle: { fontSize: '13px' } },
    { headerName: 'File Size',     field: 'fileSize',     flex: 1, minWidth: 100, cellStyle: { fontSize: '13px' } },
    {
      headerName: 'Uploaded Date', field: 'uploadedDate', flex: 1, filter: 'agDateColumnFilter', filterParams: dateFilterParamsLong, cellStyle: { fontSize: '13px' },
      // Default: most recently modified on top
      sort: 'desc', sortIndex: 0,
      comparator: (a: string, b: string) => {
        const ta = a && a !== '\u2013' ? new Date(a).getTime() : 0;
        const tb = b && b !== '\u2013' ? new Date(b).getTime() : 0;
        return ta - tb;
      },
    },
    { headerName: 'Action',        field: 'action',       flex: 1.2, minWidth: 120, sortable: false, cellRenderer: docActionEmployeeRenderer },
  ];

  docGridOptions = { ...NORMAL_GRID, rowHeight: 44 };

  // ─── Navigation ────────────────────────────────────────────────────────────

  // ─── Grid cell actions ─────────────────────────────────────────────────────

  onDocGridCellClick(event: any): void {
    // ag-grid cellClicked event structure: event = { event: MouseEvent, rowIndex, data, ... }
    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;
    
    const target = (mouseEvent.target as HTMLElement)?.closest?.('[data-action]') as HTMLElement | null;
    if (!target) return;
    
    const action = target.getAttribute('data-action');
    const doc    = this.policyDocuments.find(d => String(d.id) === target.getAttribute('data-id'));
    if (!doc) return;
    
    if (action === 'preview')  this.openDocPreview(doc);
    if (action === 'download') this.downloadDoc(doc);
  }

  private openDocPreview(doc: PolicyDocument): void {
    if (!doc.documentUrl) return;
    this.leaveService.downloadProxiedDocument(doc.documentUrl).subscribe({
      next: (blob) => {
        this.dialog.open<DocPreviewDialogComponent, DocPreviewDialogData>(
          DocPreviewDialogComponent,
          { data: { src: blob, fileName: doc.name || 'document' }, maxWidth: '95vw', panelClass: 'lm-dialog-panel' }
        );
      },
      error: () => window.open(doc.documentUrl, '_blank', 'noopener,noreferrer'),
    });
  }

  private downloadDoc(doc: PolicyDocument): void {
    if (!doc.documentUrl) return;
    this.leaveService.downloadProxiedDocument(doc.documentUrl).subscribe({
      next: (blob) => this.triggerBlobDownload(blob, doc.name || 'document'),
      error: () => window.open(doc.documentUrl, '_blank', 'noopener,noreferrer'),
    });
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor  = document.createElement('a');
    anchor.href     = blobUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    setTimeout(() => {
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
  }
}
